"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SalesOrderItemBuilt = void 0;
const class_validator_1 = require("class-validator");
const _ = require("lodash");
const BlueprintStockedProduct_1 = require("./BlueprintStockedProduct");
const ProductBlueprint_1 = require("./ProductBlueprint");
const SalesOrderItem_1 = require("./SalesOrderItem");
const restrictions_1 = require("./restrictions");
const pricingRules_1 = require("./pricingRules");
const TSalesOrderItemType_1 = require("../types/TSalesOrderItemType");
const byCategoryPriorityAscending = (productA, productB) => productA.product.category.priority - productB.product.category.priority;
/**
 * The item and quantity that relates to the product the customer bought
 * and customised from a product blueprint.
 */
class SalesOrderItemBuilt extends SalesOrderItem_1.SalesOrderItem {
    get productId() {
        return this.productBlueprint.productId;
    }
    get category() {
        return this.productBlueprint.category;
    }
    get type() {
        return TSalesOrderItemType_1.TSalesOrderItemType.Built;
    }
    get title() {
        return this.productBlueprint.title;
    }
    get price() {
        return this.calculatePrice();
    }
    /**
     * Main selection for the built product
     */
    get mainSelection() {
        // the main selection should be in the highest category
        const { sortedSelectedProducts, productBlueprint: { pricingRules } } = this;
        if (sortedSelectedProducts.length === 0) {
            return null;
        }
        // HACK
        // A main selection is dictated by:
        // 1. having a "complementary by category" rule
        // 2. the complementary level is at lease 1
        // 3. matches the same category as the top level sorted selection
        const mainSelection = sortedSelectedProducts.find((selection) => {
            const pricingRuleThatDictatesMainSelection = pricingRules.find((pricingRule) => pricingRule.name === pricingRules_1.TPricingRuleName.complementaryByCategory &&
                pricingRule.quantity > 0 &&
                selection.product.category.name === pricingRule.category);
            return pricingRuleThatDictatesMainSelection !== undefined;
        });
        return mainSelection !== undefined ? mainSelection.product : null;
    }
    /**
     * TODO: move to zam specific
     * Get the Trt Id for the corresponding product based on the main selection.
     * Knowing the main selection is essential to determine the Trt Id for the
     * salesOrderItemBuilt as there is one Id for each combination of the
     * product and the main selection in the TRT system.
     *
     * Taking burrito as example, there are the following Ids in the TRT system:
     *
     * title: Burrito - Beef
     * trtId: 100068.0
     * productId: FFBCBF00
     *
     * title: Burrito - Cauliflower
     * trtId: 101811.0
     * productId: FFBCCF00
     *
     * title: Burrito - Chicken
     * trtId: 100069.0
     * productId: FFBCCH00
     *
     * title: Burrito - Lamb
     * trtId: 100070.0
     * productId: FFBCLM00
     *
     * title: Burrito - Pulled Pork
     * trtId: 100071.0
     * productId: FFBCPK00
     *
     * title: Burrito - Vegetarian
     * trtId: 100072.0
     * productId: FFBCVG00
     */
    get trtId() {
        const { mainSelection, productBlueprint: { productId, trtIds } } = this;
        // if there's no main selection because of no category choices, it's generally because its a single product.
        // so we return just the main trt id related to it if it actually has one
        if (mainSelection === null) {
            return trtIds[productId] || null;
        }
        if (trtIds === undefined) {
            return null;
        }
        // return the main selection trt id or if not exists the single product's trt id
        const trtId = trtIds[mainSelection.productId] || trtIds[productId];
        return trtId !== undefined ? trtId : null;
    }
    /**
     * Build a description of the selected products. This includes:
     *
     *  - Main selection
     *  - Base products
     *  - Extra products
     *  - Removed products
     *
     *  Any complementary items are excluded
     */
    get description() {
        return this._buildDescription(false);
    }
    /**
     * Build a description of the selected products. This includes:
     *
     *  - Main selection
     *  - Base products
     *  - Extra products
     *  - Removed products
     *
     *  Any complementary items are excluded
     */
    get fullDescription() {
        return this._buildDescription(true);
    }
    _buildDescription(includeDefaults) {
        const { mainSelection, baseProductsWithDefaults, baseProductsWithoutDefaults, defaultProducts, extraProducts, removedProducts, selectedProducts } = this;
        // add the main selection
        let description = "";
        if (mainSelection && mainSelection.title) {
            description += `${mainSelection.title}`;
        }
        // the main selection is already added above, so we remove it from the base
        const baseProductsWithoutMainSelection = baseProductsWithoutDefaults.filter((baseProduct) => (mainSelection === null || mainSelection === void 0 ? void 0 : mainSelection.productId) !== baseProduct.product.productId);
        // add base selections
        const baseDescription = this._getSelectionDescription(baseProductsWithoutMainSelection);
        if (baseDescription) {
            description += mainSelection ? " - " : "";
            description += includeDefaults
                ? `Selected: ${baseDescription}`
                : baseDescription;
        }
        let includedDescription;
        if (includeDefaults) {
            const includedProducts = baseProductsWithDefaults
                .filter((baseProduct) => (mainSelection === null || mainSelection === void 0 ? void 0 : mainSelection.productId) !== baseProduct.product.productId)
                .filter((baseProduct) => defaultProducts.find((defaultProduct) => defaultProduct.product.productId === baseProduct.product.productId))
                .filter((baseProduct) => selectedProducts.find((selectedProduct) => selectedProduct.product.productId ===
                baseProduct.product.productId));
            includedDescription = this._getSelectionDescription(includedProducts);
            if (includedDescription) {
                description += mainSelection || baseDescription ? " - " : "";
                description += `Included: ${includedDescription}`;
            }
        }
        // add extra selections
        const extraDescription = this._getSelectionDescription(extraProducts);
        if (extraDescription) {
            description +=
                mainSelection || baseDescription || includedDescription ? " - " : "";
            description += `(Extra: ${extraDescription}`;
        }
        // add any removed ingredients
        const removedDescription = this._getSelectionDescription(removedProducts);
        if (removedDescription) {
            if (extraDescription !== "") {
                description += " / ";
            }
            else {
                description +=
                    mainSelection || baseDescription || includedDescription
                        ? " - ("
                        : "(";
            }
            description += `Removed: ${removedDescription}`;
        }
        // close of extras/removals text
        if (extraDescription !== "" || removedDescription !== "") {
            description += ")";
        }
        return description;
    }
    get image() {
        return this.productBlueprint.imageUrl;
    }
    get isValid() {
        // this SalesOrderItemBuilt is deemed as valid if none of restriction rules is violated
        return this.restrictions.every((restriction) => restriction.isValid(this.productBlueprint.products, this.productBlueprint.productAvailability, this.selectedProducts));
    }
    /**
     * Selected products sorted by their category priority
     */
    get sortedSelectedProducts() {
        return this.selectedProducts.sort(byCategoryPriorityAscending);
    }
    /**
     * List default products i.e. those with a non-zero default level in blueprint
     */
    get defaultProducts() {
        return this.productBlueprint.products
            .filter(product => product.defaultLevel !== 0)
            .map(product => ({
            quantity: product.defaultLevel,
            product
        }))
            .sort(byCategoryPriorityAscending);
    }
    /**
     * List products that were not default but added by the customer
     */
    get addedProducts() {
        return this.selectedProducts
            .map(selectedProduct => {
            // check if this selected product is a default product
            const i = this.defaultProducts.findIndex(defaultProduct => defaultProduct.product.productId ===
                selectedProduct.product.productId);
            if (i === -1) {
                // this product is completely added
                return selectedProduct;
            }
            // this product is a default product, check quantity next
            if (this.defaultProducts[i].quantity >= selectedProduct.quantity) {
                // this product is not extra at all
                return null;
            }
            else {
                // this product is partially added
                return {
                    quantity: selectedProduct.quantity - this.defaultProducts[i].quantity,
                    product: selectedProduct.product
                };
            }
        })
            .filter(extraProduct => extraProduct !== null)
            .sort(byCategoryPriorityAscending);
    }
    get baseProductsWithDefaults() {
        return this.getBaseProducts(false);
    }
    get baseProductsWithoutDefaults() {
        return this.getBaseProducts(true);
    }
    /**
     * Products that are not part of the assumed build or extras but have been selected
     */
    getBaseProducts(excludeDefaults = true) {
        return this.selectedProducts
            .map(selectedProduct => {
            // not selected so remove it
            if (selectedProduct.quantity === 0) {
                return null;
            }
            // check if this selected product is a default product
            // if so then it's not a base product but part of the assumed build
            if (excludeDefaults) {
                const i = this.defaultProducts.findIndex(defaultProduct => defaultProduct.product.productId ===
                    selectedProduct.product.productId);
                if (i >= 0) {
                    return null;
                }
            }
            // product is base selection if its not default but is complementary
            const pa = this.productBlueprint.productAvailability.find(pa => pa.productId === selectedProduct.product.productId);
            if (pa === undefined) {
                return null;
            }
            const complementaryCount = pa.complementaryCount;
            if (complementaryCount === 0) {
                return null;
            }
            return {
                quantity: complementaryCount,
                product: selectedProduct.product
            };
        })
            .filter(extraProduct => extraProduct !== null)
            .sort(byCategoryPriorityAscending);
    }
    /**
     * Products that have been selected that incure an extra price
     */
    get extraProducts() {
        return this.selectedProducts
            .map(selectedProduct => {
            // not selected so remove it
            if (selectedProduct.quantity === 0) {
                return null;
            }
            // determine if product is an extra
            // this means:
            //  - product is now and extra
            const pa = this.productBlueprint.productAvailability.find(pa => pa.productId === selectedProduct.product.productId);
            if (pa === undefined) {
                return null;
            }
            const isNowExtra = pa.extraPrice > 0;
            if (!isNowExtra) {
                return null;
            }
            else {
                // the number of extra selections is the quantity minus how many are already complementary
                const complementaryCount = pa.complementaryCount;
                return {
                    quantity: selectedProduct.quantity - complementaryCount,
                    product: selectedProduct.product
                };
            }
        })
            .filter(extraProduct => extraProduct !== null)
            .sort(byCategoryPriorityAscending);
    }
    /**
     * List products that were default but removed by the customer
     */
    get removedProducts() {
        return this.defaultProducts
            .map(defaultProduct => {
            // check if this default product is selected
            const i = this.selectedProducts.findIndex(selectedProduct => selectedProduct.product.productId ===
                defaultProduct.product.productId);
            if (i === -1) {
                // this product is completely removed
                return defaultProduct;
            }
            // this product is still selected, check quantity next
            if (this.selectedProducts[i].quantity >= defaultProduct.quantity) {
                // this product is not removed at all
                return null;
            }
            else {
                // this product is partially removed
                return {
                    quantity: defaultProduct.quantity - this.selectedProducts[i].quantity,
                    product: defaultProduct.product
                };
            }
        })
            .filter(removedProduct => removedProduct !== null)
            .sort(byCategoryPriorityAscending);
    }
    /**
     * Quick lookup to find out which required sections have not been completed
     */
    get missingRequiredSectionsMap() {
        const missingRequiredSectionsMap = new Map();
        const { selectedProducts, productBlueprint: { products, productAvailability, categories, requiredCategoriesMap, productsByCategory, restrictions } } = this;
        // for each category find out whether it is required and whether it as been completed
        categories.forEach((category, index) => {
            const { name: categoryName } = category;
            if (requiredCategoriesMap.get(categoryName)) {
                const categoryProducts = productsByCategory.get(categoryName);
                // find every category restriction min max rules for the given category
                const categoryRestrictionRules = restrictions.filter((restriction) => {
                    if ((restriction.name === restrictions_1.TRestrictionRuleName.categoryMinMax &&
                        restriction.category === categoryName) ||
                        (restriction.name ===
                            restrictions_1.TRestrictionRuleName.categoryMutuallyExclusive &&
                            restriction.category === categoryName)) {
                        return true;
                    }
                    const itemRestrictionRuleInCategory = categoryProducts.some((blueprintStockedProduct) => blueprintStockedProduct.category.name === categoryName &&
                        restriction.name === restrictions_1.TRestrictionRuleName.itemMinMax &&
                        restriction.productId === blueprintStockedProduct.productId);
                    return itemRestrictionRuleInCategory;
                });
                // determine if every rule is the category is valid
                const categoryFulfilled = categoryRestrictionRules.every((restriction) => restriction.isValid(products, productAvailability, selectedProducts));
                // if there is a single restriction rule that hasn't been fulfilled then add it the missing required sections
                // indexed by category name and then the index of the category of how its displayed in the apps
                if (!categoryFulfilled) {
                    missingRequiredSectionsMap.set(categoryName, index);
                }
            }
        });
        return missingRequiredSectionsMap;
    }
    /**
     * Quick lookup to get the number of ingredients selected in a category
     */
    get quantitySelectedByCategory() {
        const quantitySelectedByCategoryMap = new Map();
        const { productBlueprint: { categories } } = this;
        categories.forEach((category) => {
            const { name: categoryName } = category;
            const categoryProductSelections = this.getSelectedProductsByCategory(categoryName);
            const selectedCategoryCount = categoryProductSelections.reduce((count, selectedProduct) => count + (selectedProduct.quantity || 0), 0);
            quantitySelectedByCategoryMap.set(categoryName, selectedCategoryCount);
        });
        return quantitySelectedByCategoryMap;
    }
    constructor(productBlueprint) {
        super();
        this.selectedProducts = [];
        this.restrictions = [];
        this.pricingRules = [];
        this.initialised = false;
        if (productBlueprint) {
            this.productBlueprint = productBlueprint;
        }
    }
    initialise(selectedProducts) {
        if (!this.initialised && this.productBlueprint) {
            // ensure that initialisation is ran only once
            this.initialised = true;
            this.initialiseRules();
            // apply selected products if provided
            // otherwise apply default level
            // lastly, apply restrictions and pricing rules
            if (Array.isArray(selectedProducts) && selectedProducts.length > 0) {
                selectedProducts.map(selectedProduct => {
                    // the BlueprintStockedProduct model that comes with the selectedProduct
                    // cannot be relied on as it might be out of date
                    // we will look for it in the latest ProductBlueprint
                    // and only add this product if it can be found
                    const blueprintStockedProduct = this.productBlueprint.products.find(product => product.productId === selectedProduct.product.productId);
                    if (blueprintStockedProduct) {
                        _.times(selectedProduct.quantity, () => this.addProduct(blueprintStockedProduct));
                    }
                });
            }
            else {
                this.productBlueprint.products.map(product => {
                    _.times(product.defaultLevel, () => this.addProduct(product));
                });
            }
            this.updateExtraStates();
            // update product availability current state based on current product
            // selection, restriction and pricing rules
            this.updateProductAvailability();
        }
    }
    initialiseRules() {
        this.restrictions = this.productBlueprint.restrictions
            .map(restriction => (0, restrictions_1.restrictionRuleFactory)(restriction))
            .filter(r => r !== null);
        this.pricingRules = this.productBlueprint.pricingRules
            .map(pricingRule => (0, pricingRules_1.pricingRuleFactory)(pricingRule))
            .filter(pr => pr !== null);
    }
    /**
     * Add a product to selection.
     */
    addProduct(product) {
        const index = this.selectedProducts.findIndex(selectedProduct => selectedProduct.product.productId === product.productId);
        let selectedProduct;
        // add product if it is not yet in selection, otherwise increment its quantity
        if (index === -1) {
            selectedProduct = { quantity: 1, product };
            this.selectedProducts.push(selectedProduct);
        }
        else {
            selectedProduct = this.selectedProducts[index];
            selectedProduct.quantity += 1;
        }
        // update product availability current state based on current product
        // selection, restriction and pricing rules
        this.updateProductAvailability();
    }
    /**
     * Remove a product from selection.
     */
    removeProduct(product, all = false // Remove product entirely disregarding quantity
    ) {
        const index = this.selectedProducts.findIndex(selectedProduct => selectedProduct.product.productId === product.productId);
        // remove product entirely from selection if there is only one left,
        // otherwise decrement its quantity
        if (index !== -1) {
            const selectedProduct = this.selectedProducts[index];
            if (selectedProduct.quantity === 1 || all) {
                this.selectedProducts.splice(index, 1);
            }
            else {
                selectedProduct.quantity -= 1;
            }
            // update product availability current state based on current product
            // selection, restriction and pricing rules
            this.updateProductAvailability();
        }
    }
    /**
     * Determines whether the given product is the main selection for this
     * ordered item
     */
    isMainSelection(product) {
        const { mainSelection } = this;
        return (mainSelection !== null && mainSelection.productId === product.productId);
    }
    /**
     * Get the main selection of a category choice
     */
    getCategoryMainSelection(category) {
        const categoryProductSelections = this.getSelectedProductsByCategory(category);
        const categoryMainSelection = categoryProductSelections.find((categoryProductSelection) => {
            return categoryProductSelection.quantity > 0;
        });
        const hasCategoryRestrictionRule = this.productBlueprint.restrictions.some((restriction) => restriction.name === restrictions_1.TRestrictionRuleName.categoryMinMax ||
            (restriction.name === restrictions_1.TRestrictionRuleName.categoryMutuallyExclusive &&
                restriction.category === category));
        return hasCategoryRestrictionRule && categoryMainSelection !== undefined
            ? categoryMainSelection.product
            : null;
    }
    /**
     * Get products in category sorted by what was last selected at the very top
     */
    getCategoryProductsSortedByLastSelection(categoryName) {
        const categoryBlueprintStockedProducts = this.productBlueprint.productsByCategory.get(categoryName);
        if (categoryBlueprintStockedProducts === undefined) {
            return [];
        }
        const selectedProductsByCategory = this.getSelectedProductsByCategory(categoryName);
        let lastSelectedProduct;
        categoryBlueprintStockedProducts.sort(categoryBlueprintStockedProduct => {
            if (lastSelectedProduct === undefined) {
                lastSelectedProduct = selectedProductsByCategory
                    .reverse()
                    .find((selectedProduct) => selectedProduct.quantity > 0);
            }
            return lastSelectedProduct !== undefined &&
                lastSelectedProduct.product.productId ===
                    categoryBlueprintStockedProduct.productId
                ? -1
                : 1;
        });
        return categoryBlueprintStockedProducts;
    }
    /**
     * Get list of ingredients in a given category sorted with the main
     * selection at the top
     */
    getCategoryProductsSortedByMainSelection(category) {
        const { productBlueprint: { productsByCategory } } = this;
        return productsByCategory
            .get(category)
            .sort((product) => this.isMainSelection(product) ? -1 : 1);
    }
    /**
     * Sort a list of ingredients the main selection at the top
     */
    sortBlueprintStockedProductsByMainSelection(blueprintStockedProducts) {
        return blueprintStockedProducts.sort((blueprintStockedProduct) => this.isMainSelection(blueprintStockedProduct) ? -1 : 1);
    }
    /**
     * Get category label based on the state of ingredients selected.
     *  - Choice is complementary
     *  - Next choices are extra
     */
    getCategoryStateLabel(category) {
        const productsSortedByMainSelection = this.getCategoryProductsSortedByMainSelection(category);
        const { productBlueprint: { pricingRules, productAvailabilityMap } } = this;
        // a category choice is complementary when:
        //  - every product in that category has the complementary category
        //  pricing rule
        //  - non of the ingredients are yet considered extra for the next selection
        const categoryChoiceIsComplementary = productsSortedByMainSelection.every((product) => {
            const hasComplementaryCategoryPricingRule = product.hasComplementaryCategoryPricingRule(pricingRules);
            const availability = productAvailabilityMap.get(product.productId);
            return hasComplementaryCategoryPricingRule && !availability.isNowExtra;
        });
        // tell user to choose their main selection when:
        //  - category choice is complementary
        // otherwise we are either adding extras or complementary ingredients
        // not selected yet
        // show different title based on whether category products are now
        // considered extra
        return categoryChoiceIsComplementary
            ? "Choose your main selection"
            : "Fancy a little bit more?";
    }
    /**
     * Determines wheter a product has been selected
     */
    productIsSelected(blueprintStockedProductId) {
        return this.selectedProducts.some(selectedProduct => selectedProduct.product.productId === blueprintStockedProductId &&
            selectedProduct.quantity > 0);
    }
    /**
     * Get all products that have been selected in a category
     */
    getSelectedProductsByCategory(category) {
        return this.selectedProducts.filter((selectedProduct) => selectedProduct.product.category.name === category);
    }
    /**
     * Get the quantity selected for a category based on the given product
     *
     *  - For a category choice this will be the number of times all products in that category have been selected
     *  - For an individual choice this will be the number of times the given product has been selected
     */
    quantitySelectedForProductBasedOnCategory(blueprintStockedProduct) {
        const { productId, category: { name } } = blueprintStockedProduct;
        const { selectedProducts, productBlueprint: { pricingRules }, quantitySelectedByCategory } = this;
        const productSelected = selectedProducts.find((selectedProduct) => selectedProduct.product.productId === productId);
        const productHasComplementaryPricingRule = blueprintStockedProduct.hasComplementaryCategoryPricingRule(pricingRules);
        if (productHasComplementaryPricingRule) {
            return quantitySelectedByCategory.get(name);
        }
        if (productSelected !== undefined) {
            return productSelected.quantity;
        }
        return 0;
    }
    serialize() {
        return Object.assign(Object.assign({}, super.serialize()), { productBlueprint: this.productBlueprint.serialize(), selectedProducts: this.selectedProducts.map((selectedProduct) => {
                return {
                    quantity: selectedProduct.quantity,
                    product: selectedProduct.product.serialize()
                };
            }) });
    }
    static unserialize(data) {
        const newSalesOrderItemBuilt = new this();
        newSalesOrderItemBuilt.salesOrderId = data.salesOrderId;
        newSalesOrderItemBuilt.productBlueprint = ProductBlueprint_1.ProductBlueprint.unserialize(data.productBlueprint);
        newSalesOrderItemBuilt.selectedProducts = data.selectedProducts.map((selectedProduct) => {
            return {
                quantity: selectedProduct.quantity,
                product: BlueprintStockedProduct_1.BlueprintStockedProduct.unserialize(selectedProduct.product)
            };
        });
        newSalesOrderItemBuilt.quantity = data.quantity;
        newSalesOrderItemBuilt.totalPrice = data.totalPrice || 0;
        newSalesOrderItemBuilt.priceDiscounted = data.priceDiscounted;
        newSalesOrderItemBuilt.initialiseRules();
        newSalesOrderItemBuilt.updateExtraStates();
        newSalesOrderItemBuilt.updateProductAvailability();
        newSalesOrderItemBuilt.initialised = true;
        return newSalesOrderItemBuilt;
    }
    async validate() {
        // needed so selection restrictions can be validated server-side
        if (!this.isValid) {
            return false;
        }
        return super.validate();
    }
    /**
     * Build a string of product selections whether they are removed or added
     * products.
     *
     * Example:
     *  - 3 X Beans, Cheese, 2 X Lettuce
     */
    _getSelectionDescription(selectedProducts) {
        return selectedProducts
            .map((selectedProduct) => {
            const { quantity } = selectedProduct;
            const { product: { title } } = selectedProduct;
            return quantity > 1 ? `${quantity} X ${title}` : title;
        })
            .filter(p => !!p)
            .join(", ");
    }
    /**
     * Calculate the total price of the sales order item based on its current build status.
     * total price =
     *  + product blueprint base price
     *  + extra cost incurred from customisation
     *  - discount incurred from pricing rules
     *
     * Use correction factor 100
     * (assuming all prices have maximum 2 digits after the decimal point)
     * to (try the best to) ensure the arithmetic only happens between integers
     * otherwise JavaScript will give you something like 0.1 + 0.2 = 0.30000000000000004.
     */
    calculatePrice() {
        const basePrice = this.productBlueprint.retailPrice;
        const extraCost = this.selectedProducts
            .map(p => p.quantity * p.product.retailPrice)
            .reduce((a, b) => (a * 100 + b * 100) / 100, 0);
        const discount = this.pricingRules
            .map(pricingRule => pricingRule.calculateDiscount(this.productBlueprint.productAvailability, this.selectedProducts))
            .reduce((a, b) => (a * 100 + b * 100) / 100, 0);
        const finalPrice = ((basePrice || 0) * 100 +
            (extraCost || 0) * 100 -
            (discount || 0) * 100) /
            100;
        /**
         * even then 'finalPrice' is still not garanteed to always have maximum 2 digits after decimal point,
         * e.g. given (40.8 * 100 + 4.1 * 100) / 100 JavaScript will yield 44.89999999999999
         * have to force precision again before returning
         */
        return parseFloat(finalPrice.toFixed(2));
    }
    /**
     * Updates the product availability current state based on the current
     * selections, restrictions and pricing rules.
     */
    updateProductAvailability() {
        this._applyRestrictions();
        this._applyPricingRules();
        this.productBlueprint.buildProductAvailabilityMap();
    }
    /**
     * Reset canAdd, canRemove flags to their initial states.
     * This is required before starting iteration of restriction rules.
     */
    _resetRestrictions() {
        this.productBlueprint.productAvailability.map(pa => {
            pa.canAdd = true;
            // can only remove selected items
            const isSelected = this.selectedProducts.some(selectedProduct => selectedProduct.product.productId === pa.productId &&
                selectedProduct.quantity > 0);
            pa.canRemove = isSelected;
            pa.disabled = false;
        });
    }
    _applyRestrictions() {
        this._resetRestrictions();
        this.restrictions.map(restriction => restriction.apply(this.productBlueprint.products, this.productBlueprint.productAvailability, this.selectedProducts));
    }
    /**
     * Run any logic based on pricing rules for the selections and
     * availability of products
     */
    _applyPricingRules() {
        this._resetComplementaryDetails();
        this.pricingRules.map((pricingRule) => pricingRule.apply(this.productBlueprint.products, this.productBlueprint.productAvailability, this.selectedProducts));
    }
    /**
     * Reset product availability complementary data
     */
    _resetComplementaryDetails() {
        this.productBlueprint.productAvailability.map((pa) => {
            pa.complementaryCount = 0;
            pa.extraPrice = 0;
            pa.isNowExtra = false;
        });
    }
    /**
     * Update availability states for whether a product is always an extra or never an extra. This can be extended eventually to other
     * properties.
     */
    updateExtraStates() {
        const { productBlueprint: { blueprintStockedProductMinMaxQuantities, blueprintStockedProductComplementaryQuantities } } = this;
        this.productBlueprint.productAvailability.map((pa) => {
            const blueprintStockedProduct = this.productBlueprint.findProductById(pa.productId);
            const { productId } = blueprintStockedProduct;
            const { min, max } = blueprintStockedProductMinMaxQuantities.get(productId);
            const complementaryQuantity = blueprintStockedProductComplementaryQuantities.get(productId);
            // a product is considered always extra if its minimum selection is greater than its complementary quantity or that
            // quantity is zero
            pa.isAlwaysExtra =
                min > complementaryQuantity || complementaryQuantity === 0;
            // a product is considered never an extra if its maximum allowed selections is never more than its complementary quantity
            pa.isNeverExtra = max <= complementaryQuantity;
        });
    }
}
__decorate([
    (0, class_validator_1.IsNotEmpty)()
], SalesOrderItemBuilt.prototype, "productBlueprint", void 0);
__decorate([
    (0, class_validator_1.IsArray)()
], SalesOrderItemBuilt.prototype, "selectedProducts", void 0);
__decorate([
    (0, class_validator_1.IsArray)()
], SalesOrderItemBuilt.prototype, "restrictions", void 0);
__decorate([
    (0, class_validator_1.IsArray)()
], SalesOrderItemBuilt.prototype, "pricingRules", void 0);
exports.SalesOrderItemBuilt = SalesOrderItemBuilt;
//# sourceMappingURL=SalesOrderItemBuilt.js.map